import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronRight, Package, PackageOpen } from "lucide-react";
import { Badge, Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { formatOrderDate } from "@/features/admin/orders/format";
import { useMembershipCountdown, useMyMembership } from "../membership.api";
import { CardHeader } from "./CardHeader";

/**
 * ═══════════════════════════════════════════════════════════════════
 * "MI PAQUETE · AUTOSHIP" — réplica del bloque de producción
 * (`DashboardSocio.tsx`), con la estética lavanda de la referencia que
 * dio Anahí (28-ago): el panel tintado con los productos del paquete en
 * mosaico, el chip "N productos", el próximo restock en grande y los dos
 * botones.
 *
 * Los datos son los del reloj de recompra: el paquete que arrancó el
 * ciclo vigente (resuelto a productos con imagen por el servidor) y su
 * fecha de vencimiento, que ES el próximo restock. Nada se inventa.
 *
 * El franquiciatario (`variant="franchisee"`) ve además la lista de
 * paquetes que ha adquirido (los mayoristas de su franquicia y los
 * personales), cada uno con su folio y su fecha.
 *
 * ── Qué es "autoship" aquí ───────────────────────────────────────────
 * El cobro recurrente todavía no existe (está en la lista de Anahí como
 * "recompra recurrente vía Stripe"). Este bloque enseña el paquete y
 * CUÁNDO toca la recompra; el botón lleva a la tienda a hacerla.
 * ═══════════════════════════════════════════════════════════════════
 */
export function PackageAutoshipCard({
  basePath,
  variant = "socio",
}: {
  basePath: "/socio" | "/mi-negocio";
  variant?: "socio" | "franchisee";
}) {
  const { t } = useTranslation();
  const clock = useMyMembership();
  const left = useMembershipCountdown(clock.data);
  const data = clock.data;
  const pkg = data?.activePackage ?? null;
  const overdue = !data || data.status === "none" || data.status === "expired";

  return (
    <section
      aria-label={t("Mi paquete · Autoship")}
      className="rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float"
    >
      <CardHeader
        icon={Package}
        item="box"
        title={variant === "franchisee" ? t("Mis paquetes · Autoship") : t("Mi paquete · Autoship")}
        to={`${basePath}/reloj`}
      />

      {/* ── El mosaico de productos sobre el panel lavanda ───────────── */}
      <div className="relative mt-4 overflow-hidden rounded-3xl border border-ds-lavender-bg-2 bg-gradient-to-br from-ds-lavender-bg via-[#E6E1F4] to-ds-lavender-bg-2 p-4">
        {clock.isPending ? (
          <div className="h-56 animate-pulse rounded-2xl bg-white/40" />
        ) : pkg && pkg.products.length > 0 ? (
          <>
            <ProductMosaic products={pkg.products} />
            <div className="mt-3 flex justify-center">
              <span className="rounded-full border border-white bg-white/90 px-3.5 py-1 text-[12.5px] font-semibold text-ds-violet-pop shadow-ds-xs">
                {t("{{n}} productos", { n: pkg.products.length })}
                <span className="ml-1.5 font-normal text-ds-ink-3">· {t("{{n}} unidades", { n: pkg.units })}</span>
              </span>
            </div>
          </>
        ) : (
          <div className="grid h-52 place-items-center text-center">
            <div className="grid justify-items-center gap-2">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/80 text-ds-violet-pop shadow-ds-xs">
                <PackageOpen className="h-5 w-5" />
              </span>
              <p className="max-w-[240px] text-[13px] leading-relaxed text-ds-ink-3">
                {t("Aún no tienes paquetes. Tu primer pedido validado arranca tu ciclo de recompra.")}
              </p>
            </div>
          </div>
        )}
      </div>

      {pkg && (
        <p className="mt-3 truncate text-[13px] text-ds-ink-3">
          <span className="font-medium text-ds-ink">{pkg.name}</span> · {t("Pedido {{folio}}", { folio: pkg.folio })}
        </p>
      )}

      {/* ── Próximo restock ──────────────────────────────────────────── */}
      <div className="mt-4 rounded-2xl border border-ds-border bg-ds-cream-2/40 px-4 py-3.5">
        <p className="text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">
          {t("Próximo restock")}
        </p>
        <p className="mt-1 flex items-baseline gap-x-2 font-mono text-ds-ink" data-testid="restock">
          <span className="text-[34px] font-semibold leading-none">{left.days}</span>
          <span className="text-[13px] text-ds-ink-3">{t("días")}</span>
          <span className="ml-1 text-[24px] font-semibold leading-none">{left.hours}</span>
          <span className="text-[13px] text-ds-ink-3">h</span>
        </p>
        <p className="mt-1 text-[13px] text-ds-ink-3">
          {data?.validUntil
            ? overdue
              ? t("Tu ciclo venció el {{date}}. Haz tu recompra para reiniciarlo.", {
                  date: formatOrderDate(data.validUntil),
                })
              : formatOrderDate(data.validUntil)
            : t("Sin ciclo activo")}
        </p>
      </div>

      {/* ── Los paquetes adquiridos (franquiciatario) ────────────────── */}
      {variant === "franchisee" && data && (
        <div className="mt-4">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">
            {t("Paquetes adquiridos")}
          </p>
          {data.acquiredPackages.length === 0 ? (
            <p className="text-[13px] text-ds-ink-3">{t("Todavía no has adquirido paquetes.")}</p>
          ) : (
            <ul className="grid gap-2">
              {data.acquiredPackages.slice(0, 4).map((item) => (
                <li
                  key={`${item.orderId}-${item.packageId}`}
                  className="flex items-center gap-3 rounded-2xl border border-ds-border bg-white px-3 py-2.5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-ds-lavender-bg text-ds-violet-pop">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Package className="h-4.5 w-4.5" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium text-ds-ink">{item.name}</p>
                    <p className="text-[12px] text-ds-ink-3">
                      {t("{{n}} productos · {{u}} unidades", { n: item.productCount, u: item.units })} ·{" "}
                      {formatOrderDate(item.validatedAt)}
                    </p>
                  </div>
                  <span
                    className="shrink-0"
                    title={item.category === "franchisee" ? t("Negocio digital (mayorista)") : t("Recompra personal")}
                  >
                    <Badge variant={item.category === "franchisee" ? "indigo" : "info"} size="sm">
                      {item.category === "franchisee" ? t("Mayorista") : t("Personal")}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Los dos botones, como en producción ──────────────────────── */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button as={Link} to="/paquetes" rightIcon={<ChevronRight />}>
          {t("Personalizar paquete")}
        </Button>
        <Button as={Link} to="/paquetes" variant="secondary">
          {t("Ver paquetes")}
        </Button>
        {overdue && data?.status !== "none" && (
          <Button as={Link} to="/shop" variant="ghost" rightIcon={<ArrowRight />}>
            {t("Hacer recompra")}
          </Button>
        )}
      </div>
    </section>
  );
}

/**
 * Hasta cuatro productos en mosaico, con las fichas ligeramente giradas
 * como en la referencia; el resto se resume en el chip de abajo. Sin
 * imagen, la ficha enseña el nombre.
 */
function ProductMosaic({
  products,
}: {
  products: ReadonlyArray<{ productId: string; name: string; imageUrl: string | null; quantity: number }>;
}) {
  const tiles = products.slice(0, 4);
  const tilt = ["-rotate-3", "rotate-2", "rotate-3", "-rotate-2"];
  return (
    <ul
      className={cn(
        "grid gap-4 px-2 py-2",
        tiles.length === 1 ? "grid-cols-1 justify-items-center" : "grid-cols-2",
      )}
    >
      {tiles.map((product, index) => (
        <li
          key={product.productId}
          className={cn(
            "relative aspect-square w-full max-w-[150px] justify-self-center overflow-hidden rounded-2xl border border-white bg-white shadow-ds-card transition hover:rotate-0 hover:shadow-ds-card-hv",
            tilt[index % tilt.length],
          )}
          title={product.name}
        >
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
          ) : (
            <span className="grid h-full place-items-center px-2 text-center text-[12px] font-medium text-ds-ink-3">
              {product.name}
            </span>
          )}
          {product.quantity > 1 && (
            <span className="absolute right-1.5 top-1.5 rounded-full bg-ds-ink/85 px-2 py-0.5 text-[11px] font-semibold text-white">
              ×{product.quantity}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
